import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Select,
  Form,
  Input,
  Spin,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import PageTitle from "../../../_components/PageTitle";
import { cardSetActions } from "../../../_actions";
import { appNotification } from "../../../_components";

const CardSet = () => {
  const { Option } = Select;
  const dispatch = useDispatch();
  const isMounted = useRef(false);
  const [confirmChangeStatus, setConfirmChangeStatus] = useState(false);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
  const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);

  const [form] = Form.useForm();
  const [id, setId] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [newName, setNewName] = useState("");
  const [newCode, setNewCode] = useState("");
  const [newEnabled, setNewEnabled] = useState(false);
  const [modalData, setModalData] = useState([]);

  const { createCardSet, getAllCardSets, editCardSet, viewCardSet } =
    cardSetActions;

  const {
    cardSets,
    allCardSetsLoading,
    allCardSetsError,
    addCardSetLoading,
    viewCardSetLoading,
    editCardSetLoading,
  } = useSelector((state) => state.cardSetState);

  const confirmChangeStatusModal = (id) => {
    setConfirmChangeStatus(true);
    setId(id);
  };

  const handleStatusChange = () => {
    const cardSet = cardSets.find((cardSet) => cardSet.id === id);
    const newEnabled = cardSet.enabled ? 0 : 1;
    const code = cardSet.code;
    const name = cardSet.name;

    const data = {
      name: name,
      code: code,
      enabled: newEnabled,
    };
    const onSuccessCallback = () => {
      dispatch(getAllCardSets());
      setConfirmChangeStatus(false);
    };
    dispatch(editCardSet(id, data, onSuccessCallback));
  };

  const handleOk = () => {
    setIsCreateModalVisible(false);
    setIsUpdateModalVisible(false);
    setConfirmChangeStatus(false);
  };

  const onFinish = (e) => {
    e.preventDefault();
    const cardSet = {
      name: newName.trim(),
      code: newCode.trim(),
    };
    const onSuccessCallback = () => {
      setNewName("");
      setNewCode("");
      setNewEnabled(false);
      setIsCreateModalVisible(false);
      dispatch(getAllCardSets());
    };

    dispatch(createCardSet(cardSet, onSuccessCallback));
  };

  const onFinishUpdate = (e) => {
    e.preventDefault();
    const cardSet = {
      name: name.trim(),
      code: code.trim(),
      enabled: enabled,
    };
    const onSuccessCallback = () => {
      setName("");
      setCode("");
      setEnabled(false);
      setIsUpdateModalVisible(false);
      dispatch(getAllCardSets());
    };

    dispatch(editCardSet(id, cardSet, onSuccessCallback));
  };

  const handleCancel = () => {
    setIsStatusModalVisible(false);
    setIsCreateModalVisible(false);
    setIsUpdateModalVisible(false);
    setConfirmChangeStatus(false);
  };

  const showModal = () => {
    setIsCreateModalVisible(true);
  };

  const handleUpdate = (id, record) => {
    const cardSet = cardSets.find((cardSet) => cardSet.id === id);
    setId(id);
    setName(cardSet.name);
    setCode(cardSet.code);
    setEnabled(cardSet.enabled);
    setIsUpdateModalVisible(true);
    setModalData(record);
  };
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
    },
    {
      title: "Status",
      dataIndex: "enabled",
      key: "enabled",
      render: (enabled, record) => {
        const status = enabled !== 0 ? "active" : "inactive";
        let color = status === "active" ? "green" : "red";

        return (
          <Space size="large">
            <Tag
              color={color}
              key={enabled}
              style={{
                width: "5rem",
                textAlign: "center",
                // height: "32px",
                padding: "0.2rem",
              }}
            >
              {status.toUpperCase()}
            </Tag>
            <Button
              type="primary"
              danger
              style={{
                width: "5rem",
                textAlign: "center",
                // height: "32px",
                padding: "0.2rem",
              }}
              onClick={() => confirmChangeStatusModal(record.id)}
            >
              {status === "active" ? "Deactivate" : "Activate"}
            </Button>
          </Space>
        );
      },
    },
    {
      title: "",
      render: (_, record) => {
        const id = record.id;
        return <Button onClick={() => handleUpdate(id, record)}>Update</Button>;
      },
    },
  ];

  useEffect(() => {
    if (allCardSetsError) {
      appNotification(
        allCardSetsError.errorMessage,
        "Unable to fetch cardSets",
        "error"
      );
    }
    if (!isMounted.current) {
      dispatch(getAllCardSets());
      isMounted.current = true;
    }
  });

  return (
    <div>
      <PageTitle>CardSet Configuration</PageTitle>
      <div
        style={{
          textAlign: "right",
          marginBottom: "1rem",
        }}
      >
        <Button type="primary" onClick={showModal}>
          <PlusOutlined />
          Create CardSet
        </Button>
      </div>
      <Modal
        title="Title"
        open={confirmChangeStatus}
        onOk={handleStatusChange}
        onCancel={handleCancel}
      >
        <p>Are you sure?</p>
      </Modal>

      <Modal
        title="Create CardSet"
        visible={isCreateModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={[
          <Button key="back" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={onFinish}
            loading={addCardSetLoading}
          >
            Submit
          </Button>,
        ]}
      >
        <Form
          form={form}
          onFinish={onFinish}
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 14 }}
          layout="horizontal"
          initialValues={{ size: "default" }}
          size={"default"}
        >
          <Form.Item label="Name">
            <Input
              name="name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Enter name"
            />
          </Form.Item>
          <Form.Item label="Code">
            <Input
              name="code"
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              placeholder="Enter code"
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Update CardSet"
        visible={isUpdateModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={[
          <Button key="back" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={onFinishUpdate}
            loading={addCardSetLoading}
          >
            Submit
          </Button>,
        ]}
      >
        <Form
          form={form}
          onFinish={onFinish}
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 14 }}
          layout="horizontal"
          initialValues={{ size: "default" }}
          size={"default"}
        >
          <Form.Item label="Name">
            <Input
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter name"
            />
          </Form.Item>
          <Form.Item label="Code">
            <Input
              name="code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Enter code"
            />
          </Form.Item>
          <Form.Item label="Status">
            <Select
              name="status"
              value={enabled}
              onChange={(e) => setEnabled(e)}
              placeholder="Select status"
            >
              <Option value={1}>Active</Option>
              <Option value={0}>Inactive</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Table
        rowKey={(record) => record.id}
        columns={columns}
        dataSource={cardSets}
        loading={allCardSetsLoading || viewCardSetLoading || editCardSetLoading}
        className="data__table"
        scroll={{ x: "max-content" }}
      />
    </div>
  );
};

export default CardSet;
